import { useSearchParams } from "react-router-dom";
import { Breadcrumbs } from "#root/components/Breadcrumbs";
import { useResponderById } from "#root/queries/responders";
import { ResponderTravelCard } from "#root/components/ResponderTravelCard";
import { ResponderTimeLog, useResponderTimeLogs } from "#root/queries/responderTimeLogs";
import { createTableColumn, DataGrid, DataGridBody, DataGridCell, DataGridHeader, DataGridHeaderCell, DataGridRow, Spinner, TableColumnDefinition } from "@fluentui/react-components";
import { getEnumLabelByValue } from "#root/utils/enums";
import { RESPONDER_STATUS_CONST } from "#root/utils/responders";
import { formatMileage, formatTimeSpan, formatToLocalTime } from "#root/services/utils";
import LoadingWrapper from "#root/components/LoadingWrapper";
import { useState } from "react";
import { LocationLiveRegular } from "@fluentui/react-icons";
import Paginator from "#root/components/Paginator";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "#root/routes/constants";

const TimeAndTravelDetails: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const responderId = searchParams.get("responderId");
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    const { data: responderData, isLoading: isLoadingResponder } = useResponderById({ id: responderId!, includeTimeLogs: true });
    const responder = responderData?.item;

    const { data: timeLogsData, isLoading: isLoadingTimeLogs } = useResponderTimeLogs({
        responderId: responderId!,
        skip: (currentPage - 1) * itemsPerPage,
        take: itemsPerPage
    });
    
    const timeLogs = timeLogsData?.items || [];
    const totalCount = timeLogsData?.totalCount ?? 0;
    const totalPages = Math.ceil(totalCount / itemsPerPage);

    const handleItemsPerPageChange = (newItemsPerPage: number): void => {
        setItemsPerPage(newItemsPerPage);
        setCurrentPage(1);
    };

    const columns: TableColumnDefinition<ResponderTimeLog>[] = [
        createTableColumn<ResponderTimeLog>({
            columnId: "timeIn",
            renderHeaderCell: () => {
                return "Time In";
            },
            renderCell: (item) => {
                return (
                    <div title={formatToLocalTime(item.timeIn)}>{formatToLocalTime(item.timeIn)}</div>
                );
            },
        }),
        createTableColumn<ResponderTimeLog>({
            columnId: "timeOut",
            renderHeaderCell: () => {
                return "Time Out";
            },
            renderCell: (item) => {
                return (
                    <div title={formatToLocalTime(item.timeOut)}>{formatToLocalTime(item.timeOut)}</div>
                );
            },
        }),
        createTableColumn<ResponderTimeLog>({
            columnId: "status",
            renderHeaderCell: () => {
                return "Status";
            },
            renderCell: (item) => {
                return (
                    <div title={getEnumLabelByValue(RESPONDER_STATUS_CONST, item.status)}>{getEnumLabelByValue(RESPONDER_STATUS_CONST, item.status)}</div>
                );
            },
        }),
        createTableColumn<ResponderTimeLog>({
            columnId: "locationFrom",
            renderHeaderCell: () => {
                return "Location From";
            },
            renderCell: (item) => {
                return (
                    <div title={item.locationFrom}>{item.locationFrom || '-'}</div>
                );
            },
        }),
        createTableColumn<ResponderTimeLog>({
            columnId: "locationTo",
            renderHeaderCell: () => {
                return "Location To";
            },
            renderCell: (item) => {
                return (
                    <div title={item.locationTo}>{item.locationTo || '-'}</div>
                );
            },
        }),
        createTableColumn<ResponderTimeLog>({
            columnId: "duration",
            renderHeaderCell: () => {
                return "Duration";
            },
            renderCell: (item) => {
                return (
                    <div title={formatTimeSpan(item.duration)}>{formatTimeSpan(item.duration)}</div>
                );
            },
        }),
        createTableColumn<ResponderTimeLog>({
            columnId: "mileage",
            renderHeaderCell: () => {
                return "Mileage";
            },
            renderCell: (item) => {
                return (
                    <div title={formatMileage(item.mileage)}>{formatMileage(item.mileage)}</div>
                );
            },
        }),
    ];

    if (!responderId) {
        navigate(ROUTES.ERROR_404);
    }

    if (isLoadingResponder) {
        return <Spinner size="large" />;
    }

    return (
        <div className="time-and-travel-details-page">
            <div className="emc-page-header">
                <div className="emc-page-header__top-row">
                    <div className="emc-page-header__breadcrumbs">
                        <Breadcrumbs currentPageName={responder?.firstName + " " + responder?.lastName} />
                    </div>
                </div>
                <div className="emc-page-header__title">
                    <h2>{responder?.firstName + " " + responder?.lastName}<div className="divider" /></h2>
                </div>
            </div>

            {responder && (
                <div>
                    <ResponderTravelCard responder={responder!} />
                </div>
            )}

            <LoadingWrapper
                isLoading={isLoadingResponder || isLoadingTimeLogs}
                isEmpty={timeLogs?.length === 0}
                emptyMessage="No time and travel logs found."
                emptyIcon={<LocationLiveRegular />}
            >
                <div className="emc-grid">
                    <DataGrid
                        className="emc-datagrid"
                        items={timeLogs}
                        columns={columns}
                        focusMode="none"
                    >
                        <DataGridHeader>
                            <DataGridRow>
                                {({ renderHeaderCell, columnId }) => (
                                    <DataGridHeaderCell className={columnId.toString()}>
                                        {renderHeaderCell()}
                                    </DataGridHeaderCell>
                                )}
                            </DataGridRow>
                        </DataGridHeader>
                        <DataGridBody<ResponderTimeLog>>
                            {({ item, rowId }) => (
                                <DataGridRow<ResponderTimeLog>
                                    key={rowId}
                                >
                                    {({ renderCell, columnId }) => (
                                        <DataGridCell className={columnId.toString()}>
                                            {renderCell(item)}
                                        </DataGridCell>
                                    )}
                                </DataGridRow>
                            )}
                        </DataGridBody>
                    </DataGrid>

                    <Paginator
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                        itemsPerPage={itemsPerPage}
                        onItemsPerPageChange={handleItemsPerPageChange}
                        totalItems={totalCount}
                        currentItems={(currentPage - 1) * itemsPerPage + 1}
                        showItemsPerPage={true}
                    />
                </div>
            </LoadingWrapper>
        </div>
    );
};

export default TimeAndTravelDetails;